import React from 'react'
import cn from 'classnames'
import { computed } from 'mobx'
import { observer } from 'mobx-react'

import UpdateStore from 'stores/update/UpdateStore'
import ConfigStore from 'stores/config/ConfigStore'
import ThemeStore from 'stores/theme/ThemeStore'

import Tooltip from 'components/tooltip/Tooltip'
import Button from 'components/button/Button'
import ShortcutDialog from 'components/shortcutDialog/ShortcutDialog'
import UpdateReadyDialog from 'components/updateReadyDialog/UpdateReadyDialog'
import Settings from 'assets/icons/settings.svg'

import styles from './settingsDropdown.scss'

interface IProps {}

interface IState {
  isOpen: boolean
  isUpdateReadyDialogOpen: boolean
  isShortcutDialogOpen: boolean
}

@observer
class SettingsDropdown extends React.Component<IProps, IState> {
  private button: React.RefObject<HTMLButtonElement> = React.createRef()

  state: IState = {
    isOpen: false,
    isUpdateReadyDialogOpen: false,
    isShortcutDialogOpen: false,
  }

  componentDidMount() {
    let disposer = computed(() => UpdateStore.isUpdateReady).observe(
      ({ newValue }) => {
        console.log({ newValue })
        if (newValue) {
          disposer()
          this.setState({ isUpdateReadyDialogOpen: true })
        }
      }
    )
  }

  handleToggle = () => {
    this.setState(state => ({ isOpen: !state.isOpen }))
  }

  handleClose = () => {
    this.setState({ isOpen: false })
  }

  handleSwitchTheme = () => {
    ThemeStore.apply(ThemeStore.theme === 'light' ? 'dark' : 'light')
    this.handleClose()
  }

  handleUpdateCheck = async () => {
    this.handleClose()
    await UpdateStore.check()
  }

  handleUpdateDownload = async () => {
    this.handleClose()
    await UpdateStore.download()
  }

  handleUpdateInstall = () => {
    this.setState({ isUpdateReadyDialogOpen: true })
    this.handleClose()
  }

  handleViewShortcuts = () => {
    this.setState({ isOpen: false, isShortcutDialogOpen: true })
  }

  render() {
    const { isOpen, isUpdateReadyDialogOpen, isShortcutDialogOpen } = this.state

    return (
      <>
        <Button
          innerRef={this.button}
          className={cn(styles.button, {
            [styles.dot]: !UpdateStore.isUpToDate || UpdateStore.isUpdateReady,
          })}
          ghost
          onClick={this.handleToggle}
          data-cy="settings-dialog"
        >
          <Settings />
        </Button>

        {isOpen && (
          <Tooltip target={this.button} onClickOutside={this.handleClose}>
            <ul className={styles.menu}>
              <li className={styles.menuItem} onClick={this.handleSwitchTheme}>
                {ThemeStore.theme === 'light' ? 'Dark theme' : 'Light theme'}
              </li>

              {UpdateStore.hasCheckedForUpdates &&
                !UpdateStore.isUpToDate &&
                !UpdateStore.isDownloading &&
                !UpdateStore.isUpdateReady && (
                  <li
                    className={cn(styles.menuItem, {
                      [styles.highlight]: true,
                    })}
                    onClick={this.handleUpdateDownload}
                  >
                    Download v{UpdateStore.latestVersion}
                  </li>
                )}
              {UpdateStore.isDownloading && (
                <li
                  className={cn(styles.menuItem, { [styles.highlight]: true })}
                  onClick={this.handleUpdateDownload}
                >
                  Downloading v{UpdateStore.latestVersion}
                </li>
              )}
              {UpdateStore.isUpdateReady && (
                <li
                  className={cn(styles.menuItem, { [styles.highlight]: true })}
                  onClick={this.handleUpdateInstall}
                >
                  Install v{UpdateStore.latestVersion}
                </li>
              )}
              {ConfigStore.updates && (
                <li
                  className={styles.menuItem}
                  onClick={this.handleUpdateCheck}
                >
                  Check for updates
                </li>
              )}
              <li className={styles.menuItem}>
                <a
                  href="https://github.com/prisma/studio/releases"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  View changelog
                </a>
              </li>
              <li
                className={styles.menuItem}
                onClick={this.handleViewShortcuts}
                data-cy="view-shortcuts"
              >
                View shortcuts
              </li>
            </ul>
          </Tooltip>
        )}

        {isUpdateReadyDialogOpen && (
          <UpdateReadyDialog
            onClose={() => this.setState({ isUpdateReadyDialogOpen: false })}
          />
        )}
        {isShortcutDialogOpen && (
          <ShortcutDialog
            onClose={() => this.setState({ isShortcutDialogOpen: false })}
          />
        )}
      </>
    )
  }
}

export default SettingsDropdown
